import { BeakerIcon } from '@heroicons/react/24/outline';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface TestModeBannerProps {
  isTest: boolean;
  className?: string;
  variant?: 'compact' | 'default' | 'prominent';
  showDescription?: boolean;
}

const TestModeBanner = ({
  isTest,
  className,
  variant = 'default',
  showDescription = false,
}: TestModeBannerProps) => {
  if (!isTest) return null;

  const variantStyles = {
    compact: 'px-2 py-1',
    default: 'px-3 py-2',
    prominent: 'px-4 py-3',
  };

  const iconSizes = {
    compact: 'h-4 w-4',
    default: 'h-5 w-5',
    prominent: 'h-6 w-6',
  };

  const iconInnerSizes = {
    compact: 'h-3 w-3',
    default: 'h-3.5 w-3.5',
    prominent: 'h-4 w-4',
  };

  const textSizes = {
    compact: 'text-[10px]',
    default: 'text-xs',
    prominent: 'text-sm',
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg border border-amber-200/60 bg-gradient-to-r from-amber-50 to-orange-50',
            variantStyles[variant],
            className,
          )}
        >
          <div
            className={cn(
              'flex items-center justify-center rounded-md bg-amber-100',
              iconSizes[variant],
            )}
          >
            <BeakerIcon
              className={cn('text-amber-600', iconInnerSizes[variant])}
            />
          </div>
          <span
            className={cn('font-medium text-amber-700', textSizes[variant])}
          >
            Test Mode
          </span>
          {(showDescription || variant !== 'compact') && (
            <span
              className={cn(
                'text-amber-500',
                variant === 'compact' ? 'text-[9px]' : 'text-[10px]',
              )}
            >
              Not processed for delivery
            </span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs text-xs">
        <p className="font-medium">Test Mode Active</p>
        <p className="mt-1 text-slate-500">
          This item will not be printed or mailed. Use the Progress Test button
          to simulate status changes.
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

export { TestModeBanner };
